'use client';

import { maybeArrayUnwrap, proformApiQuery } from '@eliadmin/core/src/api/proform/proform-api';
import { ProfileInvoiceItem } from '@eliadmin/core/src/components/profile/profile-invoice-item';
import { GenericLoader } from '@eliadmin/core/src/components/ui/generic-loader';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@eliadmin/core/src/components/ui/select';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { useQuery } from '@tanstack/react-query';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { useMemo } from 'react';

export default function InvoicesPage() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const customer = useCustomerStore(s => s.me!.customer);
  const { t } = useTranslations();
  const locale = useLocaleStore(s => s.currentLocale);
  const pathname = usePathname();

  const currentYear = new Date().getFullYear();
  const selectedYear = useMemo(() => {
    const yearFromParams = searchParams.get('year');
    if (!yearFromParams) return currentYear.toString();

    const parsedYear = parseInt(yearFromParams, 10);
    if (isNaN(parsedYear) || parsedYear < 2000 || parsedYear > currentYear) {
      return currentYear.toString();
    }

    return parsedYear.toString();
  }, [searchParams, currentYear]);

  const handleYearChange = (year: string) => {
    const parsedYear = parseInt(year, 10);
    if (!isNaN(parsedYear) && parsedYear >= 2005 && parsedYear <= currentYear) {
      router.push(`${pathname}?year=${year}`, { scroll: false });
    }
  };

  const years = useMemo(() => {
    const yearArray = [];
    for (let year = 2005; year <= currentYear; year++) {
      yearArray.push(year.toString());
    }
    return yearArray.reverse();
  }, [currentYear]);

  const smakoInvoices = useQuery({
    queryKey: ['smako-invoices', customer.email, locale, selectedYear],
    queryFn: () =>
      proformApiQuery('ELI_GET_INVOICES', {
        e_mail: customer.email,
        od_data: `${selectedYear}-01-01`,
        do_data:
          selectedYear === String(currentYear)
            ? new Date().toISOString().split('T')[0]
            : `${selectedYear}-12-31`,
      }),
  });

  const smakoInvoicesArr = maybeArrayUnwrap(smakoInvoices.data?.root.invoices?.invoice ?? [])
    .filter(d => d !== undefined)
    .filter(d => d.druh_dokladu === '10');

  if (smakoInvoices.isLoading) {
    return <GenericLoader />;
  }

  return (
    <div className="flex flex-col gap-4 @container">
      <div className="flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
        <h2 className="text-2xl font-bold">{t('text1228')}</h2>
        <Select value={selectedYear} onValueChange={handleYearChange}>
          <SelectTrigger className="w-full sm:w-[180px]">
            <SelectValue placeholder="Select year" />
          </SelectTrigger>
          <SelectContent className="max-h-[200px] overflow-x-auto">
            {years.map(year => (
              <SelectItem key={year} value={year}>
                {year}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      {/* header */}
      {smakoInvoicesArr.length > 0 && (
        <div className="hidden flex-row items-center gap-4 p-4 @xl:flex">
          <span className="flex-1 text-start text-sm font-semibold text-muted-foreground">
            {t('text108')}
          </span>
          <span className="flex-1 text-start text-sm font-semibold text-muted-foreground">
            {t('text31')}
          </span>
          <span className="flex-1 text-start text-sm font-semibold text-muted-foreground">
            {t('text121')}
          </span>
          <span className="flex-1 text-start text-sm font-semibold text-muted-foreground">
            {t('text132')}
          </span>
          <span className="flex-1 text-start text-sm font-semibold text-muted-foreground">
            {t('text419')}
          </span>
          <span className="flex-1 text-start text-sm font-semibold text-muted-foreground"></span>
        </div>
      )}
      {/* content */}
      <div className="-mx-4 flex flex-col @xl:mx-0 @xl:gap-4">
        {smakoInvoicesArr.map(invoice => (
          <ProfileInvoiceItem key={invoice.doklad} invoice={invoice} />
        ))}
      </div>

      {!smakoInvoicesArr.length && (
        <p className="text-bold text-sm text-muted-foreground">{t('text445')}</p>
      )}
    </div>
  );
}
